import { DefaultRow, Table as TableCanoe, TableColumn } from 'canoe';
import { compose, connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useMemo, useState } from 'react';

import { SortOrder } from '@candu-ai/analytics/types';
import { CollectionCount, CollectionType } from '@candu-ai/collections/types';
import { useNavigation } from 'commons/hooks';
import TimeAgo from 'dashboard/components/TimeAgo';
import { COLLECTIONS_PATH } from 'dashboard/pages/Collections/constants';

interface CollectionTableProps {
  collections: CollectionType[];
  collectionCounts: CollectionCount[];
}

enum ColumnKeys {
  CollectionId = 'id',
  CollectionName = 'collectionName',
  LastUpdated = 'lastUpdatedAt',
  Records = 'records',
}

interface CollectionTableData extends DefaultRow {
  id: string;
  collectionName: string;
  lastUpdatedAt: Date;
  records: number;
}

const CollectionTableImpl: React.FC<CollectionTableProps> = ({
  collections,
  collectionCounts,
}: CollectionTableProps) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [sortValue, setSortValue] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigation();

  const rows = useMemo(
    () =>
      collections.map((collection) => ({
        [ColumnKeys.CollectionId]: collection.id,
        [ColumnKeys.CollectionName]: collection.collectionName,
        [ColumnKeys.LastUpdated]: collection.lastUpdatedAt,
        [ColumnKeys.Records]:
          collectionCounts.find((collectionCount) => collectionCount.id === collection.id)?.count ||
          0,
      })),
    [collections],
  );

  const filteredRows = useMemo(
    () =>
      rows
        .filter((collection) =>
          collection[ColumnKeys.CollectionName].toLowerCase().includes(searchValue.toLowerCase()),
        )
        .sort((a, b) => {
          if (sortValue === 'asc') {
            return a[ColumnKeys.LastUpdated] > b[ColumnKeys.LastUpdated] ? 1 : -1;
          }
          if (sortValue === 'desc') {
            return a[ColumnKeys.LastUpdated] < b[ColumnKeys.LastUpdated] ? 1 : -1;
          }
          return 0;
        }),
    [rows, searchValue, sortValue],
  );

  const columns: TableColumn[] = useMemo(() => {
    return [
      {
        key: ColumnKeys.CollectionName,
        dataIndex: ColumnKeys.CollectionName,
        title: 'Name',
        isSearchable: true,
        colSpan: 2,
      },
      {
        key: ColumnKeys.LastUpdated,
        dataIndex: ColumnKeys.LastUpdated,
        title: 'Last Updated',
        render: (text: string, record: CollectionTableData) =>
          record[ColumnKeys.LastUpdated] ? (
            <TimeAgo date={record[ColumnKeys.LastUpdated]} />
          ) : (
            'Never'
          ),
        isSortable: true,
        initialSortOrder: SortOrder.DESC,
      },
      {
        key: ColumnKeys.Records,
        dataIndex: ColumnKeys.Records,
        title: 'Records',
      },
    ];
  }, []);

  return (
    <TableCanoe<CollectionTableData>
      rows={filteredRows}
      columns={columns}
      onRow={(record) => ({
        onClick: () => navigate(`${COLLECTIONS_PATH}/${record[ColumnKeys.CollectionId]}`),
      })}
      onFilterChange={(column, value) => {
        setSearchValue(value ?? '');
      }}
      sortingColumn={ColumnKeys.LastUpdated}
      sortingOrder={sortValue}
      onSortChange={(column, sort) => setSortValue(sort ?? 'desc')}
      searchingColumn={ColumnKeys.CollectionName}
      searchingValue={searchValue}
    />
  );
};

const withCollections = connectWithHammock({
  endpoint: ENDPOINTS.collections,
  resourceName: 'collections',
});

const withCounts = connectWithHammock({
  endpoint: ENDPOINTS.collectionCount,
  resourceName: 'collectionCounts',
});

export const CollectionList = compose(withCollections, withCounts)(CollectionTableImpl);
